<template>
  <v-dialog
    max-width="600"
    :value="value"
    :persistent="loading"
    :no-click-animation="loading"
    @input="$emit('input:dialog', $event)"
  >
    <v-card class="nx-dashboard-shortcuts-wizard">
      <v-card-title class="headline">{{ $t('message.dashboard.shortcuts.createTitle') }}</v-card-title>
      <v-card-text class="pa-0">
        <v-stepper v-model="step" color="info" class="elevation-0">
          <v-stepper-header class="elevation-0">
            <v-stepper-step color="info" :complete="step > 1" :step="1">
              {{ $t('message.dashboard.shortcuts.wizard.steps.resource') }}
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step color="info" :complete="step > 2" :step="2">
              {{ $t('message.dashboard.shortcuts.wizard.steps.action') }}
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step color="info" :complete="step > 3" :step="3">
              {{ $t('message.dashboard.shortcuts.wizard.steps.filters') }}
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>

            <v-stepper-content step="1" class="pt-0">
              <h2 class="nx-heading--level-1" v-text="$t(`message.dashboard.shortcuts.wizard.title.resource`)"></h2>
              <v-radio-group v-model="draft.resource">
                <v-radio
                  v-for="(resourceConfig, resource) in config"
                  :key="resource"
                  :value="resource"
                  :label="resourceConfig.label | translate | capitalize"
                />
              </v-radio-group>
            </v-stepper-content>

            <v-stepper-content step="2" class="pt-0">
              <h2 class="nx-heading--level-1" v-text="$t(`message.dashboard.shortcuts.wizard.title.action`)"></h2>
              <v-radio-group v-model="draft.action">
                <v-radio
                  v-for="action in selectedResourceActions"
                  :key="action"
                  :value="action"
                  :label="$t(`message.dashboard.shortcuts.actions.${action}`)"
                />
              </v-radio-group>
            </v-stepper-content>

            <v-stepper-content step="3" class="pt-0">
              <component
                v-if="selectedActionComponent"
                :is="selectedActionComponent"
                :value="draft.filters"
                :wizard-config="wizardConfig"
                @input="draft.filters = $event"
              />
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" v-show="previousButtonVisible" small flat @click="prevStep">Anterior</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="secondary" small flat @click="$emit('input:dialog')">
          {{ $t('message.common.cancel') }}
        </v-btn>
        <v-btn color="secondary" small flat @click="nextStep" :disabled="nextButtonDisabled" :loading="loading">
          {{ nextButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { config } from '@/modules/dashboard/shortcuts/config';
import uuid from 'uuid/v4';
import { entityIcon } from '@/modules/entity/utils';

const STEPS = {
  RESOURCE: 1,
  ACTION: 2,
  FILTERS: 3,
};

export default {
  name: 'nx-dashboard-shortcuts-wizard',
  props: {
    value: Boolean,
    cardId: {
      type: String,
      required: true,
    },
    wizardConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      step: STEPS.RESOURCE,
      stepToDraft: {
        [STEPS.RESOURCE]: 'resource',
        [STEPS.ACTION]: 'action',
        [STEPS.FILTERS]: 'filters',
      },
      loading: false,
      config,
      draft: {
        resource: null,
        action: null,
        filters: null,
      },
    };
  },
  computed: {
    /** @return {ShortcutWizardResourceConfig|undefined} */
    selectedResource() {
      return this.config[this.draft.resource];
    },

    /** @return {Object|undefined} */
    selectedActionComponent() {
      if (!this.selectedResource) {
        return undefined;
      }
      return this.selectedResource.actions[this.draft.action];
    },

    /** @return {string[]} */
    selectedResourceActions() {
      if (!this.selectedResource) {
        return [];
      }
      return Object.keys(this.selectedResource.actions);
    },

    previousButtonVisible() {
      return this.step > STEPS.RESOURCE;
    },
    nextButtonDisabled() {
      return !this.draft[this.stepToDraft[this.step]];
    },
    nextButtonText() {
      return (this.readyToCreateShortcut || this.step === STEPS.FILTERS)
        ? this.$t('message.common.create')
        : this.$t('message.common.next');
    },

    readyToCreateShortcut() {
      if (!this.selectedResource) return false;
      if (this.selectedResourceActions.length === 0) return true;
      return this.step === STEPS.FILTERS && this.draft.action && this.draft.filters;
    },
  },
  methods: {
    nextStep() {
      if (this.readyToCreateShortcut) {
        this.createShortcut();
        return;
      }

      this.step += 1;
    },
    prevStep() {
      this.draft[this.stepToDraft[this.step]] = null;
      this.step -= 1;
    },
    updateField(field, value) {
      this.draft[field] = value;
    },

    async createShortcut() {
      if (!this.readyToCreateShortcut) return;
      this.loading = true;

      if (this.draft.resource === 'site') {
        this.draft.action = 'access';
        const url = this.$router.resolve({
          name: 'entity.editor',
          params: {
            entity: 'site',
            id: this.$store.getters.siteId,
          },
        });
        this.draft.filters = {
          url: url.href,
          type: 'site',
          text: this.$t('message.entity.type.site'),
        };
      }

      let shortcut;
      if (this.draft.action === 'access') {
        shortcut = {
          id: uuid(),
          type: 'dashboard-link-shortcut',
          icon: this.draft.filters.icon || entityIcon(this.draft.filters.type),
          text: this.draft.filters.text,
          url: this.draft.filters.url,
        };
      } else if (this.draft.action === 'create') {
        shortcut = {
          id: uuid(),
          type: 'dashboard-create-entity-shortcut',
          icon: 'add',
          text: `wizard.new.${this.draft.filters}`,
          entity: this.draft.filters,
        };
      }

      await this.$store.dispatch('dashboard/CREATE_SHORTCUT', {
        cardId: this.cardId,
        shortcut,
      });

      this.loading = false;
      this.$emit('input:dialog', false);
    },
  },
};
</script>
